/**
 * Custom-frontend manifest accessors.
 *
 * The manifest is delivered by the masterdata API under the
 * `custom_frontend_manifest` key. Helpers read it synchronously from the
 * Redux store via `getStore()`. Until masterdata has loaded, the helpers
 * return empty arrays / undefined — call sites either render their fallback
 * or wait until the masterdata-driven re-render arrives.
 */

import { getStore } from '../../store/storeRef';

export interface CustomFrontendApp {
  appId: string;
  url: string;
}

export type CustomFrontendMenuTarget = 'inApp' | 'newTab';

export interface CustomFrontendMenuEntry {
  id: string;
  appId: string;
  label: string;
  icon?: string;
  parent: string[];
  target: CustomFrontendMenuTarget;
  iframeRoute: string;
  permissions?: string[];
}

export interface CustomFrontendSlot {
  id: string;
  appId: string;
  iframeRoute: string;
  permissions?: string[];
  presentation?: 'inline' | 'overlay';
}

interface CustomFrontendManifest {
  apps: CustomFrontendApp[];
  menu: CustomFrontendMenuEntry[];
  slots: CustomFrontendSlot[];
}

function readManifest(): CustomFrontendManifest | undefined {
  const store = getStore();
  if (!store) return undefined;
  return store.getState()?.masterData?.custom_frontend_manifest;
}

export function getCustomFrontendApps(): CustomFrontendApp[] {
  return readManifest()?.apps ?? [];
}

export function getCustomFrontendMenu(): CustomFrontendMenuEntry[] {
  return readManifest()?.menu ?? [];
}

export function findCustomFrontendApp(appId: string): CustomFrontendApp | undefined {
  return getCustomFrontendApps().find(a => a.appId === appId);
}

export function findCustomFrontendMenuById(id: string): CustomFrontendMenuEntry | undefined {
  return getCustomFrontendMenu().find(m => m.id === id);
}

export function customFrontendMenuHasChildren(id: string): boolean {
  return getCustomFrontendMenu().some(m => m.parent.includes(id));
}

export function findCustomFrontendSlot(id: string): CustomFrontendSlot | undefined {
  return readManifest()?.slots?.find(s => s.id === id);
}
